import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { MediaSessionControl } from 'capacitor-media-session-control';

const useMediaSession = ({
  currentItem,
  isCountdown,
  isRadioMode,
  isPlaying,
  videoElement, // Changed from videoRef
  onExitRadio,
  onNext,
  setRadioMuted,
}) => {
  useEffect(() => {
    if (!currentItem || isCountdown) return;

    // Only init if actually playing, otherwise don't put anything in the lock screen
    if (!isPlaying && !videoElement?.currentTime) return;

    const title = currentItem.title || 'Unknown Title';
    const artist = currentItem.speaker || 'BAWS.in Audio';
    const album = 'Babasaheb Ambedkar Speeches Collection Volume 40';
    const artwork =
      currentItem.thumbnail || 'https://baws.in/assets/images/logo.png';
    const duration = videoElement?.duration || 0;
    const position = videoElement?.currentTime || 0;

    if (Capacitor.isNativePlatform()) {
      MediaSessionControl.initMediaSession({
        title,
        artist,
        album,
        cover: artwork,
        duration: duration,
        position: position,
        isPlaying: isPlaying,
      }).catch(console.error);

      // We explicitly update metadata to ensure the notification reflects the new track.
      // Sometimes initMediaSession ignores changes if it thinks the session is already active.
      MediaSessionControl.updateMetadata({
        title,
        artist,
        album,
        cover: artwork,
        duration: duration,
      }).catch(console.error);

      MediaSessionControl.updatePlaybackState({
        state: isPlaying ? 'playing' : 'paused',
        position: position,
        playbackSpeed: 1,
      }).catch(console.error);
    } else if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: title,
        artist: artist,
        album: album,
        artwork: [
          {
            src: artwork,
            sizes: '512x512',
            type: 'image/png',
          },
        ],
      });
      navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
    }

    const allActions = [
      'play',
      'pause',
      'seekbackward',
      'seekforward',
      'seekto',
      'previoustrack',
      'nexttrack',
      'stop',
    ];

    let targetHandlers = {};

    if (isRadioMode) {
      targetHandlers = {
        pause: () => {
          if (videoElement) {
            videoElement.muted = true;
            videoElement.pause();
          }
          if (setRadioMuted) setRadioMuted(true);
          if (Capacitor.isNativePlatform()) {
            MediaSessionControl.updatePlaybackState({
              state: 'paused',
              position: videoElement?.currentTime || 0,
              playbackSpeed: 1,
            }).catch(console.error);
          } else {
            navigator.mediaSession.playbackState = 'paused';
          }
        },
        stop: () => {
          if (videoElement) {
            videoElement.muted = true;
            videoElement.pause();
          }
          if (setRadioMuted) setRadioMuted(true);
          if (Capacitor.isNativePlatform()) {
            MediaSessionControl.stop().catch(console.error);
          } else {
            navigator.mediaSession.playbackState = 'paused';
          }
        },
      };
    } else {
      targetHandlers = {
        play: () => videoElement?.play(),
        pause: () => videoElement?.pause(),
        stop: () => videoElement?.pause(),
        nexttrack: () => {
          if (onNext) onNext();
        },
        seekbackward: (details) => {
          const skip = details?.seekOffset || 10;
          if (videoElement)
            videoElement.currentTime = Math.max(
              videoElement.currentTime - skip,
              0
            );
        },
        seekforward: (details) => {
          const skip = details?.seekOffset || 10;
          if (videoElement)
            videoElement.currentTime = Math.min(
              videoElement.currentTime + skip,
              videoElement.duration
            );
        },
        seekto: (details) => {
          if (videoElement && details?.seekTime) {
            if (details.fastSeek && 'fastSeek' in videoElement) {
              videoElement.fastSeek(details.seekTime);
            } else {
              videoElement.currentTime = details.seekTime;
            }
          }
        },
      };
    }

    let nativeListener = null;

    if (Capacitor.isNativePlatform()) {
      MediaSessionControl.addListener('mediaSessionEvent', (payload) => {
        const action = payload.event;

        if (action === 'play' && targetHandlers.play) targetHandlers.play();
        else if (action === 'pause' && targetHandlers.pause)
          targetHandlers.pause();
        else if (action === 'stop' && targetHandlers.stop)
          targetHandlers.stop();
        else if (action === 'next' && targetHandlers.nexttrack)
          targetHandlers.nexttrack();
        else if (action === 'previous' && targetHandlers.previoustrack)
          targetHandlers.previoustrack();
        else if (action === 'seekTo' && targetHandlers.seekto) {
          targetHandlers.seekto({ seekTime: payload.position });
        }
      })
        .then((listener) => {
          nativeListener = listener;
        })
        .catch(console.error);
    } else if ('mediaSession' in navigator) {
      allActions.forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(
            action,
            targetHandlers[action] || null
          );
        } catch (e) {
          // Ignore
        }
      });
    }

    return () => {
      if (Capacitor.isNativePlatform() && nativeListener) {
        nativeListener.remove();
      }
    };
  }, [
    currentItem,
    isCountdown,
    isRadioMode,
    isPlaying,
    onExitRadio,
    onNext,
    videoElement,
    setRadioMuted,
  ]);
};

export default useMediaSession;
